import { has } from '@/commonFunctions/utils'
import { PlayerInfoSection } from '@/components/common'
import { More, People, Person } from '@/components/icons'
import { BRACKET_MODIFY } from '@/constants/Common'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { BracketPlayer, ClubStaff, Player } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { Dispatch, Fragment, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

type Props = {
  className?: string
  item: BracketPlayer | Player
  isNavigation?: boolean
  showCheckBox?: boolean
  checked?: Player | null
  isChecked?: boolean
  isCheckInput?: boolean
  setChecked?: Dispatch<SetStateAction<Player | null>>
  onChangeCheckbox?: () => void
  failedReason?: string
  isShowMore?: boolean
  directors?: ClubStaff[]
  organizers?: ClubStaff[]
  onWithdrawRefund?: (player: BracketPlayer, isWithDraw?: boolean) => void
}

export const BracketPlayerItem = ({
  className,
  item,
  isNavigation,
  showCheckBox,
  checked,
  isChecked,
  setChecked,
  onChangeCheckbox,
  failedReason,
  isCheckInput,
  isShowMore,
  directors,
  organizers,
  onWithdrawRefund,
}: Props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const onNavigateProfile = (playerId: number) => {
    if (isNavigation) navigate(`${ROUTE_PLAYER_PROFILE}/${playerId}`)
  }

  const onPlayerCheck = () => {
    if (item.id !== checked?.id && setChecked) setChecked(item as Player)
    if (item.id === checked?.id) return
  }

  return (
    <label
      className={`flex flex-row gap-x-4 bg-white rounded-2xl p-4 ${
        isNavigation || showCheckBox ? 'cursor-pointer' : 'cursor-default'
      } ${className}`}
    >
      {showCheckBox && (
        <div className="flex items-center">
          <input
            type="checkbox"
            className={`w-5 h-5`}
            checked={!isCheckInput ? checked?.id === item.id : isChecked || false}
            onChange={() => {
              if (!isCheckInput) onPlayerCheck()
              if (isCheckInput && onChangeCheckbox) onChangeCheckbox()
            }}
          />
        </div>
      )}
      <div
        className={`flex flex-col flex-1 gap-y-3 ${isNavigation || showCheckBox ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => onNavigateProfile(item.id)}
      >
        <div className="flex justify-between relative">
          {isShowMore && directors && organizers && (
            <div className="absolute right-0 -top-3">
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button>
                  <More className="w-6 h-6" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute top-4 right-0 mt-2 w-36 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="flex flex-col gap-y-1 px-1 py-1 ">
                      <Menu.Item>
                        {({ active }) =>
                          has(permissions, BRACKET_MODIFY) &&
                          user?.id !== item.id &&
                          ![...organizers, ...directors]?.find((host) => host.userId === item.id) ? (
                            <button
                              className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                active && 'bg-slate-300'
                              } `}
                              onClick={() => {
                                if (onWithdrawRefund) onWithdrawRefund(item as BracketPlayer)
                              }}
                            >
                              {t('event.withdrawRefund')}
                            </button>
                          ) : (
                            <button
                              className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                active && 'bg-slate-300'
                              } `}
                              onClick={() => {
                                if (onWithdrawRefund) onWithdrawRefund(item as BracketPlayer, true)
                              }}
                            >
                              {t('event.withdraw')}
                            </button>
                          )
                        }
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          )}

          <PlayerInfoSection player={item} />
          <div className="flex gap-x-1 items-center">
            <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-11 h-9  flex flex-col justify-center items-center">
              <People className="text-navy-900" />
              <span className="text-xs text-navy-900 font-medium">{item?.ratings?.doubles}</span>
            </div>
            <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-10 h-9  flex flex-col justify-center items-center">
              <Person className="text-navy-900" />
              <span className="text-xs text-navy-900 font-medium">{item?.ratings?.singles}</span>
            </div>
          </div>
        </div>
        {failedReason && <div className="flex text-boldOrange text-sm">{failedReason}</div>}
      </div>
    </label>
  )
}
